/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import React, { useCallback } from 'react';
import { useGqlContext } from '@search/gql-client/src';
import { useGql2Loader } from '@search/gql-client/src/useGql2';
import { useM2AuthContext } from '@search/auth/src/M2AuthProvider';
import { isProfessional } from '@search/auth/src/helpers/isProfessional';
import { QueryErrors } from '../types/QueryErrors';
import { ICustomerFull, ICustomer } from '../types/ICustomer';
import { ISearch, ISearchWithCustomer } from '../types/ISearch';
import { AgentGetCustomersQuery } from './AgentGetCustomersQuery';
import { AgentGetCustomersQuery$variables, AgentGetCustomersQuery$data } from './__generated__/AgentGetCustomersQuery.graphql';

type IAgent = NonNullable<AgentGetCustomersQuery$data['agent']>;
type ICustomerSearches = NonNullable<IAgent['searchesList'][number]>;
type ICustomerFavorite = NonNullable<IAgent['favoritesList'][number]>;
type ICustomerItem = NonNullable<IAgent['customersList'][number]>;
type ISearchRaw = NonNullable<ICustomerSearches['searches'][number]>

export interface IUseAgentReturnType {
    agentData?: Record<ICustomer['id'], ICustomerFull>;
    agentLoading: boolean;
    // reload: () => void;
    clearCache: () => void;
}

function useAgentFetcher() {
    const gql = useGqlContext();

    return useCallback((vars: AgentGetCustomersQuery$variables, opts?: { signal?: AbortSignal }) => {
        return gql.client<
            AgentGetCustomersQuery$variables,
            AgentGetCustomersQuery$data & QueryErrors
        >(AgentGetCustomersQuery, vars, opts);
    }, [ gql ]);
}

export const useAgent = (): IUseAgentReturnType => {
    const auth = useM2AuthContext();
    const loader = useGql2Loader(useAgentFetcher(), { cachePrefix: 'AgentFavorites', authTrack: true });
    const res = auth.auth.isAuthenticated && isProfessional(auth.auth.user.currentRoleType) ? loader({}) : undefined;
    const agent = res?.data?.agent;
    const agentData = React.useMemo(() => agent?.customersList ? normalizeCustomers(agent) : undefined, [ agent ]);

    return {
        agentData,
        agentLoading: res?.loading ?? false,
        clearCache: loader.clearCache,
    };
};

const normalizeCustomers = (agent?: IAgent | null): Record<ICustomer['id'], ICustomerFull> => {
    const normalizeCustomerFavorites = (agent?.favoritesList ?? [])
        .reduce<Record<ICustomer['id'], Partial<ICustomerFavorite>>>(
            (acc, customerFavorite) => {
                acc[customerFavorite.customerId] = { ...customerFavorite };

                return acc;
            },
            {}
        );

    const normalizeCustomerSearches = (agent?.searchesList ?? [])
        .reduce<Record<ICustomer['id'], Partial<ICustomerSearches>>>(
            (acc, customerSearches) => {
                acc[customerSearches.customerId] = { ...customerSearches };

                return acc;
            },
            {}
        );

    const customers = (agent?.customersList ?? []).map(el => el)
        .sort((a: ICustomerItem, b: ICustomerItem) => {
            const tmpA = a.updated ?? a.created;
            const tmpB = b.updated ?? b.created;

            if (tmpB < tmpA) {
                return -1;
            }
            if (tmpB > tmpA) {
                return 1;
            }

            return 0;
        })
        .reduce<Record<ICustomer['id'], ICustomerFull>>((acc, customer) => {
            acc[customer.id] = {
                ...customer,
                favoritesCount: normalizeCustomerFavorites[customer.id]?.favoritesCount ?? 0,
                favoritesList: (normalizeCustomerFavorites[customer.id]?.favorites ?? [])
                    .map(el => el.offerId),
                searchesCount: normalizeCustomerSearches[customer.id]?.searchesCount ?? 0,
                searchesList: (normalizeCustomerSearches[customer.id]?.searches ?? [])
                    .map(normalizeSearch(customer)) as ISearchWithCustomer[]
            };

            return acc;
        }, {});

    return customers;
};

const normalizeSearch = (customer?: ICustomer) => (search: ISearchRaw): ISearchWithCustomer | ISearch => {
    const { subscription, ...rest } = search;
    const emails = subscription?.emails ?? [];
    const isSubscription = emails.length > 0;

    if (customer !== undefined) {
        return {
            customer,
            emails: emails as string[],
            isSubscription,
            ...rest
        };
    }

    return {
        emails: emails as string[],
        isSubscription,
        ...rest
    };
};
